// ManualDocPage — /send/manual route: send your OWN (PDF) document to a tenancy
// for signing (WEL-48 manual-doc).
//
// Flow:
//   1. Pick a property → unit, "Load current residents" (reused from Renewal) →
//      an editable signer roster (pre-filled from AppFolio). Toggle "Include WHP
//      as a signer" to add the WHP countersigner.
//   2. Name the document (required) + optional description.
//   3. Upload a PDF. We render it page-by-page with pdfjs-dist into canvases.
//   4. DRAG to place signature / date / initials field boxes onto the pages and
//      assign each to a signer (a resident, or WHP). Boxes are editable + removable.
//   5. Send → POST a multipart body (the PDF + a JSON `payload`) to
//      /api/manual-doc/send, which builds the DocuSeal submission FROM the PDF with
//      coordinate fields, persists a manual_document signature record, and emails
//      the residents their branded signing links.
//
// Coordinates are captured as NORMALIZED 0..1 fractions of each page (x,y =
// top-left corner; w,h = size), 1-based page — exactly the DocuSeal `areas` shape
// the backend expects (apps/api/src/lib/docuseal.ts DocuSealFieldArea).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as pdfjs from 'pdfjs-dist';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { SearchSelect } from '@/components/SearchSelect';
import { toast } from '@/components/Toast';
import { ApiError } from '@/lib/api';
import {
  listCurrentResidents,
  listProperties,
  listUnits,
  type PropertyOption,
  type RenewalResidentOption,
  type UnitOption,
  unitOptionLabel,
} from '@/hooks/useLeaseLifecycle';
import {
  analyzeManualDocument,
  sendManualDocument,
  MANUAL_DOC_FIELD_TYPES,
  type ManualDocAdvisoryOverrideInput,
  type ManualDocAdvisoryWarning,
  type ManualDocFieldType,
  type ManualDocFieldInput,
} from '@/lib/manualDocClient';
import { ConfirmDialog } from '@/components/ConfirmDialog';

// pdfjs needs its worker. Vite bundles this URL import and serves the worker file.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// A field box is rendered to ~120×40 px at the page's display scale by default
// (when staff click rather than drag). Normalized below before send.
const RENDER_SCALE = 1.25;

interface ResidentRow extends RenewalResidentOption {
  key: number;
}

let nextKey = 1;
const blankRow = (): ResidentRow => ({
  key: nextKey++,
  tenantId: '',
  firstName: '',
  lastName: '',
  email: '',
  tenantType: null,
  dateOfBirth: null,
});

/** Short label for the AppFolio TenantType, for the roster badge. */
function tenantTypeLabel(type: string | null): string | null {
  if (!type) return null;
  const t = type.trim().toLowerCase();
  if (t === 'co-signer' || t === 'cosigner') return 'Cosigner';
  if (t === 'financially responsible') return 'Resident';
  return type;
}

// A placed field. Coordinates are normalized 0..1 fractions of the page. `signer`
// is a resident ROW KEY (so it survives roster edits) or the literal 'whp'; we map
// the row key → 0-based resident index at send time.
interface PlacedField {
  id: number;
  page: number; // 1-based
  type: ManualDocFieldType;
  x: number;
  y: number;
  w: number;
  h: number;
  signer: number | 'whp'; // resident row key, or 'whp'
}

let nextFieldId = 1;

const FIELD_TYPE_LABEL: Record<ManualDocFieldType, string> = {
  signature: 'Signature',
  date: 'Date',
  initials: 'Initials',
};

// A stable color per signer slot so boxes read at a glance which party fills them.
const SIGNER_COLORS = [
  '#2563eb', // blue
  '#16a34a', // green
  '#9333ea', // purple
  '#ea580c', // orange
  '#0891b2', // cyan
  '#db2777', // pink
];
function colorForSigner(signer: number | 'whp', residentKeys: number[]): string {
  if (signer === 'whp') return '#475569'; // slate — WHP
  const idx = residentKeys.indexOf(signer);
  return SIGNER_COLORS[idx >= 0 ? idx % SIGNER_COLORS.length : 0]!;
}

/** One rendered PDF page + its overlay where staff drag field boxes. */
function PdfPage({
  doc,
  pageNumber,
  fields,
  residentKeys,
  signerLabelFor,
  activeType,
  activeSigner,
  onPlace,
  onRemove,
}: {
  doc: PDFDocumentProxy;
  pageNumber: number;
  fields: PlacedField[];
  residentKeys: number[];
  signerLabelFor: (signer: number | 'whp') => string;
  activeType: ManualDocFieldType;
  activeSigner: number | 'whp';
  onPlace: (f: Omit<PlacedField, 'id'>) => void;
  onRemove: (id: number) => void;
}): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  // In-progress drag rectangle (pixels, relative to the wrap), or null.
  const [drag, setDrag] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);

  // Render the page to the canvas once the doc/page is ready.
  useEffect(() => {
    let cancelled = false;
    let task: { cancel: () => void } | null = null;
    (async () => {
      const page = await doc.getPage(pageNumber);
      if (cancelled) return;
      const viewport = page.getViewport({ scale: RENDER_SCALE });
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      setSize({ w: Math.floor(viewport.width), h: Math.floor(viewport.height) });
      const renderTask = page.render({ canvasContext: ctx, viewport });
      task = renderTask;
      try {
        await renderTask.promise;
      } catch {
        /* render cancelled on unmount/scale change — ignore */
      }
    })();
    return () => {
      cancelled = true;
      try { task?.cancel(); } catch { /* noop */ }
    };
  }, [doc, pageNumber]);

  const relPoint = (e: React.MouseEvent): { x: number; y: number } => {
    const rect = wrapRef.current!.getBoundingClientRect();
    return {
      x: Math.min(Math.max(e.clientX - rect.left, 0), rect.width),
      y: Math.min(Math.max(e.clientY - rect.top, 0), rect.height),
    };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    const p = relPoint(e);
    setDrag({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!drag) return;
    const p = relPoint(e);
    setDrag({ ...drag, x1: p.x, y1: p.y });
  };
  const finishDrag = () => {
    if (!drag || !size) { setDrag(null); return; }
    let px = Math.min(drag.x0, drag.x1);
    let py = Math.min(drag.y0, drag.y1);
    let pw = Math.abs(drag.x1 - drag.x0);
    let ph = Math.abs(drag.y1 - drag.y0);
    // A click (no real drag) drops a default-sized box anchored at the point.
    if (pw < 8 || ph < 8) {
      pw = Math.min(120, size.w);
      ph = Math.min(40, size.h);
    }
    // Clamp inside the page.
    px = Math.min(px, size.w - pw);
    py = Math.min(py, size.h - ph);
    onPlace({
      page: pageNumber,
      type: activeType,
      x: px / size.w,
      y: py / size.h,
      w: pw / size.w,
      h: ph / size.h,
      signer: activeSigner,
    });
    setDrag(null);
  };

  const pageFields = fields.filter((f) => f.page === pageNumber);

  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', marginBottom: 'var(--space-1)' }}>
        Page {pageNumber}
      </div>
      <div
        ref={wrapRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={finishDrag}
        onMouseLeave={() => drag && finishDrag()}
        style={{
          position: 'relative',
          display: 'inline-block',
          lineHeight: 0,
          border: '1px solid var(--border)',
          cursor: 'crosshair',
          userSelect: 'none',
          width: size ? size.w : undefined,
          height: size ? size.h : undefined,
          background: 'var(--bg-subtle, #f1f5f9)',
        }}
      >
        <canvas ref={canvasRef} style={{ display: 'block' }} />

        {/* Placed field boxes */}
        {size && pageFields.map((f) => {
          const color = colorForSigner(f.signer, residentKeys);
          return (
            <div
              key={f.id}
              title={`${FIELD_TYPE_LABEL[f.type]} — ${signerLabelFor(f.signer)}`}
              style={{
                position: 'absolute',
                left: f.x * size.w,
                top: f.y * size.h,
                width: f.w * size.w,
                height: f.h * size.h,
                border: `2px solid ${color}`,
                background: `${color}22`,
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                lineHeight: 1.1,
                color,
                fontWeight: 600,
                overflow: 'hidden',
                pointerEvents: 'auto',
              }}
            >
              <span style={{ padding: '0 2px', textAlign: 'center' }}>
                {FIELD_TYPE_LABEL[f.type]}
                <br />
                {signerLabelFor(f.signer)}
              </span>
              <button
                type="button"
                aria-label="Remove field"
                onMouseDown={(e) => { e.stopPropagation(); }}
                onClick={(e) => { e.stopPropagation(); onRemove(f.id); }}
                style={{
                  position: 'absolute',
                  top: -9,
                  right: -9,
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  border: 'none',
                  background: color,
                  color: '#fff',
                  fontSize: 11,
                  lineHeight: '18px',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                ×
              </button>
            </div>
          );
        })}

        {/* Live drag preview */}
        {drag && (
          <div
            style={{
              position: 'absolute',
              left: Math.min(drag.x0, drag.x1),
              top: Math.min(drag.y0, drag.y1),
              width: Math.abs(drag.x1 - drag.x0),
              height: Math.abs(drag.y1 - drag.y0),
              border: '2px dashed var(--accent, #2563eb)',
              background: 'rgba(37,99,235,0.12)',
              boxSizing: 'border-box',
              pointerEvents: 'none',
            }}
          />
        )}
      </div>
    </div>
  );
}

/**
 * One signer row, used on BOTH sides of the line. `ordinal` is set below the line
 * so the chain reads "1st, 2nd, …" — above the line the order is meaningless and
 * numbering it would imply a sequence that doesn't exist.
 */
function SignerRow(props: {
  row: ResidentRow;
  idx: number;
  ordinal?: number;
  onChange: (patch: Partial<ResidentRow>) => void;
  onRemove: () => void;
  onMove: () => void;
  moveLabel: string;
  onUp?: (() => void) | undefined;
  onDown?: (() => void) | undefined;
}): React.JSX.Element {
  const { row: r, idx, ordinal, onChange, onRemove, onMove, moveLabel, onUp, onDown } = props;
  const email = (r.email ?? '').trim();
  const emailInvalid = !!email && !EMAIL_RE.test(email);
  const badge = tenantTypeLabel(r.tenantType);
  const label = ordinal ? `Signer ${ordinal} (in turn)` : `Signer ${idx + 1}`;
  return (
    <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
      {ordinal !== undefined && (
        <span
          style={{
            paddingTop: 8,
            minWidth: '1.25rem',
            fontSize: 'var(--text-xs)',
            color: 'var(--fg-muted)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {ordinal}.
        </span>
      )}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <input
          type="text"
          className="input"
          placeholder={`${label} first name`}
          value={r.firstName}
          onChange={(e) => onChange({ firstName: e.target.value })}
          aria-label={`${label} first name`}
        />
        {badge && (
          <span className="pill" data-tone="neutral" style={{ alignSelf: 'flex-start' }}>
            {badge}
          </span>
        )}
      </div>
      <input
        type="text"
        className="input"
        style={{ flex: 1 }}
        placeholder={`${label} last name`}
        value={r.lastName}
        onChange={(e) => onChange({ lastName: e.target.value })}
        aria-label={`${label} last name`}
      />
      <div style={{ flex: 1.4, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <input
          type="email"
          className="input"
          placeholder="tenant@example.com"
          value={r.email ?? ''}
          onChange={(e) => onChange({ email: e.target.value })}
          aria-label={`${label} email`}
          aria-invalid={emailInvalid}
        />
        {emailInvalid && <span className="helper" data-state="error">Enter a valid email.</span>}
        {!email && <span className="helper" data-state="error">Email required to send.</span>}
      </div>
      {onUp && (
        <button type="button" className="btn" data-variant="ghost" data-size="sm" data-icon-only
          aria-label={`Move ${label} earlier`} title="Sign earlier" onClick={onUp}>
          ↑
        </button>
      )}
      {onDown && (
        <button type="button" className="btn" data-variant="ghost" data-size="sm" data-icon-only
          aria-label={`Move ${label} later`} title="Sign later" onClick={onDown}>
          ↓
        </button>
      )}
      <button type="button" className="btn" data-variant="ghost" data-size="sm"
        aria-label={`${moveLabel} — ${label}`} title={moveLabel} onClick={onMove}>
        {ordinal === undefined ? '↓ In turn' : '↑ Same time'}
      </button>
      <button type="button" className="btn" data-variant="ghost" data-size="sm" data-icon-only
        aria-label={`Remove ${label}`} onClick={onRemove}>
        ×
      </button>
    </div>
  );
}

export function ManualDocPage(): React.JSX.Element {
  // --- property / unit picker -----------------------------------------------
  const [properties, setProperties] = useState<PropertyOption[]>([]);
  const [propsLoading, setPropsLoading] = useState(true);
  const [propsError, setPropsError] = useState<string | null>(null);
  const [propertyId, setPropertyId] = useState('');
  const [units, setUnits] = useState<UnitOption[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitsError, setUnitsError] = useState<string | null>(null);
  const [unitId, setUnitId] = useState('');

  // --- signer roster --------------------------------------------------------
  const [residents, setResidents] = useState<ResidentRow[]>([]);
  const [residentsLoading, setResidentsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [includeWhp, setIncludeWhp] = useState(false);
  /** BELOW the line — signers who go in turn, each invited after the one before. */
  const [sequential, setSequential] = useState<ResidentRow[]>([]);

  // --- document + fields ----------------------------------------------------
  const [documentName, setDocumentName] = useState('');
  const [documentDescription, setDocumentDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [fields, setFields] = useState<PlacedField[]>([]);
  const [activeType, setActiveType] = useState<ManualDocFieldType>('signature');
  const [activeSigner, setActiveSigner] = useState<number | 'whp'>('whp');

  const [busy, setBusy] = useState(false);

  // --- pre-send advisory (CC Phase 3 #4 — the Brownplex/Lords guard) --------
  // The uploaded PDF is analyzed in the background while staff place fields
  // (wrong-property text / already-executed audit page). ADVISORY ONLY: any
  // warnings surface in a modal at send click with "Send anyway"; an analysis
  // failure means "no advisory", never a blocked send.
  const [advisory, setAdvisory] = useState<{
    status: 'idle' | 'checking' | 'done' | 'failed';
    warnings: ManualDocAdvisoryWarning[];
  }>({ status: 'idle', warnings: [] });
  const advisoryPromise = useRef<Promise<ManualDocAdvisoryWarning[]> | null>(null);
  const [advisoryModalOpen, setAdvisoryModalOpen] = useState(false);
  const [overrideNote, setOverrideNote] = useState('');
  /** True while the send click is waiting for an in-flight analysis. */
  const [advisoryWait, setAdvisoryWait] = useState(false);

  // Load properties on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listProperties();
        if (!cancelled) setProperties(list);
      } catch (err) {
        if (!cancelled) setPropsError(err instanceof Error ? err.message : 'Failed to load properties');
      } finally {
        if (!cancelled) setPropsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load units when the property changes; reset the unit-scoped roster.
  useEffect(() => {
    setResidents([]);
    setLoaded(false);
    if (!propertyId) {
      setUnits([]);
      setUnitId('');
      setUnitsError(null);
      return;
    }
    let cancelled = false;
    setUnitsLoading(true);
    setUnitsError(null);
    listUnits(propertyId)
      .then((list) => {
        if (cancelled) return;
        setUnits(list);
        setUnitId((prev) => (list.some((u) => u.unitId === prev) ? prev : (list[0]?.unitId ?? '')));
      })
      .catch((err) => {
        if (!cancelled) setUnitsError(err instanceof Error ? err.message : 'Failed to load units');
      })
      .finally(() => {
        if (!cancelled) setUnitsLoading(false);
      });
    return () => { cancelled = true; };
  }, [propertyId]);

  useEffect(() => {
    setResidents([]);
    setLoaded(false);
  }, [unitId]);

  const updateResident = (key: number, patch: Partial<RenewalResidentOption>) => {
    setResidents((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const handleLoadResidents = async () => {
    if (!propertyId || !unitId) return;
    setResidentsLoading(true);
    try {
      const { residents: list } = await listCurrentResidents(propertyId, unitId);
      setResidents(list.map((r) => ({ ...r, key: nextKey++ })));
      setLoaded(true);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? ((err.body as { error?: string } | null)?.error ?? `Could not load residents (HTTP ${err.status})`)
          : err instanceof Error
            ? err.message
            : 'Could not load current residents';
      toast.error(msg, { title: 'Load residents' });
    } finally {
      setResidentsLoading(false);
    }
  };

  // Residents that can actually receive a signing request (name + valid email).
  const validResidents = useMemo(
    () => residents.filter((r) => r.firstName.trim() && EMAIL_RE.test((r.email ?? '').trim())),
    [residents],
  );
  const validSequential = useMemo(
    () => sequential.filter((r) => r.firstName.trim() && EMAIL_RE.test((r.email ?? '').trim())),
    [sequential],
  );
  const residentKeys = useMemo(
    () => [...residents.map((r) => r.key), ...sequential.map((r) => r.key)],
    [residents, sequential],
  );

  // The premises the document is ABOUT — sent as `premises` so the email + sign
  // page name the property/unit (a bespoke-comms requirement). Prefer the selected
  // unit's address; fall back to the property's. The signer half already loads
  // property/unit, so this is the address staff just picked.
  const premises = useMemo(() => {
    const unit = units.find((u) => u.unitId === unitId);
    if (unit?.address?.trim()) return unit.address.trim();
    const prop = properties.find((p) => p.propertyId === propertyId);
    return (prop?.address ?? '').trim();
  }, [units, unitId, properties, propertyId]);

  // Kick the pre-send advisory analysis whenever the PDF or the target
  // property/unit context changes. Results are ready by send click in the
  // common case (staff spend far longer placing fields than the analysis
  // takes); handleSend awaits the in-flight promise otherwise.
  useEffect(() => {
    advisoryPromise.current = null;
    if (!file) {
      setAdvisory({ status: 'idle', warnings: [] });
      return;
    }
    let cancelled = false;
    setAdvisory({ status: 'checking', warnings: [] });
    const p = analyzeManualDocument(file, {
      ...(propertyId ? { propertyId } : {}),
      ...(premises ? { premises } : {}),
    })
      .then((r) => {
        if (!cancelled) setAdvisory({ status: 'done', warnings: r.warnings });
        return r.warnings;
      })
      .catch(() => {
        // Advisory only — an analysis failure is "no advisory", never a block.
        if (!cancelled) setAdvisory({ status: 'failed', warnings: [] });
        return [] as ManualDocAdvisoryWarning[];
      });
    advisoryPromise.current = p;
    return () => {
      cancelled = true;
    };
  }, [file, propertyId, premises]);

  // Display label for a signer slot (used on the boxes + the picker).
  const signerLabelFor = useCallback(
    (signer: number | 'whp'): string => {
      if (signer === 'whp') return 'WHP';
      const r = [...residents, ...sequential].find((x) => x.key === signer);
      if (!r) return 'Signer';
      const name = `${r.firstName} ${r.lastName}`.trim();
      return name || 'Signer';
    },
    [residents, sequential],
  );

  // Keep the active signer valid as the roster + WHP toggle change.
  useEffect(() => {
    const validKeys = new Set<number | 'whp'>(
      [...residents, ...sequential].map((r) => r.key),
    );
    if (includeWhp) validKeys.add('whp');
    if (!validKeys.has(activeSigner)) {
      setActiveSigner(includeWhp ? 'whp' : (residents[0]?.key ?? sequential[0]?.key ?? 'whp'));
    }
  }, [residents, sequential, includeWhp, activeSigner]);

  // Drop fields assigned to a signer who no longer exists (removed resident, or
  // WHP toggled off).
  useEffect(() => {
    setFields((prev) =>
      prev.filter((f) =>
        f.signer === 'whp'
          ? includeWhp
          : [...residents, ...sequential].some((r) => r.key === f.signer),
      ),
    );
  }, [residents, sequential, includeWhp]);

  // Load the PDF into pdfjs when the file changes.
  const handleFile = useCallback((f: File | null) => {
    setPdfDoc(null);
    setPdfError(null);
    setFields([]);
    setFile(f);
    if (!f) return;
    if (f.type !== 'application/pdf') {
      setPdfError('The document must be a PDF.');
      setFile(null);
      return;
    }
    (async () => {
      try {
        const buf = await f.arrayBuffer();
        const doc = await pdfjs.getDocument({ data: new Uint8Array(buf) }).promise;
        setPdfDoc(doc);
      } catch (err) {
        setPdfError(err instanceof Error ? err.message : 'Could not read that PDF.');
        setFile(null);
      }
    })();
  }, []);

  const addField = useCallback((f: Omit<PlacedField, 'id'>) => {
    setFields((prev) => [...prev, { ...f, id: nextFieldId++ }]);
  }, []);
  const removeField = useCallback((id: number) => {
    setFields((prev) => prev.filter((f) => f.id !== id));
  }, []);

  // Every displayed signer must carry a valid email — we do NOT silently drop a
  // roster signer who lacks one. A signer the staffer doesn't want must be removed
  // explicitly (the × button), not left email-less to vanish from the recipients.
  const everySignerValid =
    residents.length > 0 &&
    residents.every((r) => r.firstName.trim() && EMAIL_RE.test((r.email ?? '').trim()));

  const canSend =
    !busy &&
    !!file &&
    !!pdfDoc &&
    documentName.trim().length > 0 &&
    everySignerValid &&
    fields.length > 0;

  /** Map placed-field signers (row keys / 'whp') → the backend's resident INDEX
   *  / 'whp', using the SAME order as the residents payload (validResidents).
   *  Returns null (with a toast) when a field points at an unusable signer. */
  const buildOutFields = (): ManualDocFieldInput[] | null => {
    const indexByKey = new Map<number, number>();
    validResidents.forEach((r, i) => indexByKey.set(r.key, i));
    const seqIndexByKey = new Map<number, number>();
    validSequential.forEach((r, i) => seqIndexByKey.set(r.key, i));

    const outFields: ManualDocFieldInput[] = [];
    for (const f of fields) {
      if (f.signer === 'whp') {
        outFields.push({ type: f.type, page: f.page, x: f.x, y: f.y, w: f.w, h: f.h, signer: 'whp' });
      } else {
        const seqIdx = seqIndexByKey.get(f.signer);
        if (seqIdx !== undefined) {
          outFields.push({
            type: f.type, page: f.page, x: f.x, y: f.y, w: f.w, h: f.h,
            signer: { seq: seqIdx },
          });
          continue;
        }
        const idx = indexByKey.get(f.signer);
        if (idx === undefined) {
          // Field belongs to a resident who lacks a valid email (excluded). Block.
          toast.error(
            `A field is assigned to "${signerLabelFor(f.signer)}", who needs a valid email or should be removed.`,
            { title: 'Cannot send' },
          );
          return null;
        }
        outFields.push({ type: f.type, page: f.page, x: f.x, y: f.y, w: f.w, h: f.h, signer: idx });
      }
    }
    if (includeWhp === false && outFields.some((f) => f.signer === 'whp')) {
      toast.error('A field is assigned to WHP, but WHP is not included as a signer.', { title: 'Cannot send' });
      return null;
    }
    return outFields;
  };

  /** The actual send. `override` carries the staffer's "Send anyway" past the
   *  advisory warnings (logged server-side); null = no warnings to override. */
  const doSend = async (override: ManualDocAdvisoryOverrideInput | null) => {
    if (!file) return;
    const outFields = buildOutFields();
    if (!outFields) return;

    setBusy(true);
    try {
      const result = await sendManualDocument(file, {
        documentName: documentName.trim(),
        ...(documentDescription.trim() ? { documentDescription: documentDescription.trim() } : {}),
        ...(premises ? { premises } : {}),
        ...(validSequential.length
          ? {
              sequential: validSequential.map((r) => ({
                firstName: r.firstName.trim(),
                lastName: r.lastName.trim(),
                email: (r.email ?? '').trim(),
                ...(r.tenantId ? { tenantId: r.tenantId } : {}),
              })),
            }
          : {}),
        residents: validResidents.map((r) => ({
          ...(r.tenantId ? { tenantId: r.tenantId } : {}),
          firstName: r.firstName.trim(),
          lastName: r.lastName.trim(),
          email: (r.email ?? '').trim(),
        })),
        includeWhpSigner: includeWhp,
        fields: outFields,
        ...(override ? { advisoryOverride: override } : {}),
      });
      toast.success(
        `“${documentName.trim()}” sent — ${result.sent} resident${result.sent === 1 ? '' : 's'} emailed a signing link.`,
        { title: 'Document sent' },
      );
      // Reset the document half (keep the property/unit + roster for a quick resend).
      setAdvisoryModalOpen(false);
      setOverrideNote('');
      setFile(null);
      setPdfDoc(null);
      setFields([]);
      setDocumentName('');
      setDocumentDescription('');
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? ((err.body as { error?: string } | null)?.error ?? `Could not send (HTTP ${err.status})`)
          : err instanceof Error
            ? err.message
            : 'Could not send the document';
      toast.error(msg, { title: 'Send' });
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async () => {
    if (!canSend || !file) return;
    // Validate the field → signer mapping BEFORE the advisory modal, so a
    // fixable roster problem surfaces first (same order as the classic flow).
    if (!buildOutFields()) return;

    // Advisory results are normally ready by now (analysis ran while fields
    // were placed); if it's still in flight, wait for it — the server bounds
    // the analysis hard, so this always resolves.
    let warnings = advisory.warnings;
    if (advisory.status === 'checking' && advisoryPromise.current) {
      setAdvisoryWait(true);
      try {
        warnings = await advisoryPromise.current;
      } finally {
        setAdvisoryWait(false);
      }
    }
    if (warnings.length > 0) {
      setOverrideNote('');
      setAdvisoryModalOpen(true);
      return;
    }
    await doSend(null);
  };

  /** "Send anyway" in the advisory modal — send with the override recorded. */
  const confirmAdvisoryOverride = () => {
    const codes = [...new Set(advisory.warnings.map((w) => w.code))];
    const note = overrideNote.trim();
    void doSend({ warnings: codes, ...(note ? { note } : {}) });
  };

  // The signer options for the field-assignment picker.
  const signerOptions: Array<{ value: string; label: string }> = [
    ...residents.map((r) => ({ value: String(r.key), label: signerLabelFor(r.key) })),
    ...sequential.map((r) => ({
      value: String(r.key),
      label: `${signerLabelFor(r.key)} (in turn)`,
    })),
    ...(includeWhp ? [{ value: 'whp', label: 'WHP' }] : []),
  ];

  const numPages = pdfDoc?.numPages ?? 0;

  return (
    <div style={{ maxWidth: 980 }}>
      <div style={{ marginBottom: 'var(--space-7)' }}>
        <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
          Manual document
        </h1>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', marginTop: 'var(--space-2)' }}>
          Upload your own PDF, place signature fields by dragging them onto the pages, pick the
          signers, and send it to a unit&apos;s current tenants for signing.
        </p>
      </div>

      {propsError && (
        <div className="alert" data-tone="danger" role="alert" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="alert-body">{propsError}</div>
        </div>
      )}

      <div className="stack">
        {/* 1. Property + unit picker */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Property &amp; unit</h3>
          </div>
          <div className="stack-sm">
            <div className="field">
              <label htmlFor="prop-select">Property</label>
              <SearchSelect
                id="prop-select"
                options={properties.map((p) => ({
                  value: p.propertyId,
                  label: p.name,
                  sublabel: p.address,
                }))}
                value={propertyId}
                onChange={setPropertyId}
                disabled={propsLoading}
                placeholder={propsLoading ? 'Loading…' : 'Select or search by name or address…'}
                emptyText="No matching properties"
              />
            </div>
            <div className="field">
              <label htmlFor="unit-select">Unit</label>
              <select
                id="unit-select"
                className="input"
                value={unitId}
                onChange={(e) => setUnitId(e.target.value)}
                disabled={!propertyId || unitsLoading}
              >
                <option value="">
                  {!propertyId
                    ? 'Select a property first…'
                    : unitsLoading
                      ? 'Loading units…'
                      : units.length === 0
                        ? 'No units found for this property'
                        : 'Select a unit…'}
                </option>
                {units.map((u) => (
                  <option key={u.unitId} value={u.unitId}>
                    {unitOptionLabel(u)}
                    {u.name && u.address ? ` — ${u.address}` : ''}
                  </option>
                ))}
              </select>
              {unitsError && <span className="helper" data-state="error">{unitsError}</span>}
            </div>
            <button
              type="button"
              className="btn"
              data-variant="secondary"
              data-size="sm"
              onClick={() => void handleLoadResidents()}
              disabled={!propertyId || !unitId || residentsLoading}
              style={{ alignSelf: 'flex-start' }}
            >
              {residentsLoading ? 'Loading residents…' : 'Load current residents'}
            </button>
          </div>
        </section>

        {/* 2. Signer roster + WHP toggle */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Signers</h3>
          </div>
          {!loaded ? (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              Pick a property + unit and choose “Load current residents” to pull the current tenants.
              You can also add signers manually.
            </p>
          ) : (
            <>
              {residents.length === 0 && (
                <div className="alert" data-tone="warning" role="note" style={{ marginBottom: 'var(--space-3)' }}>
                  <div className="alert-body">
                    AppFolio shows no current adult occupants or cosigners for this unit. Add signers
                    manually below.
                  </div>
                </div>
              )}
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', margin: '0 0 var(--space-3)' }}>
                Pre-filled from AppFolio (editable). Each signer needs a valid email to receive the
                signing request.
              </p>

              {/* ABOVE THE LINE — everyone here is emailed at once and can sign in
                  any order. */}
              <p style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-medium)', color: 'var(--fg)', margin: '0 0 var(--space-2)' }}>
                Sign at the same time
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {residents.map((r, idx) => (
                  <SignerRow
                    key={r.key}
                    row={r}
                    idx={idx}
                    onChange={(patch) => updateResident(r.key, patch)}
                    onRemove={() => setResidents((prev) => prev.filter((x) => x.key !== r.key))}
                    onMove={() => {
                      setResidents((prev) => prev.filter((x) => x.key !== r.key));
                      setSequential((prev) => [...prev, r]);
                    }}
                    moveLabel="Move below the line"
                  />
                ))}
                <button
                  type="button"
                  className="btn"
                  data-variant="secondary"
                  data-size="sm"
                  onClick={() => setResidents((prev) => [...prev, blankRow()])}
                  style={{ alignSelf: 'flex-start' }}
                >
                  + Add signer
                </button>
              </div>

              {/* THE LINE. Above it: one parallel batch. Below it: a chain, each
                  person invited only once the one before them has signed. */}
              <div
                className="cluster"
                style={{
                  gap: 'var(--space-3)',
                  alignItems: 'center',
                  margin: 'var(--space-5) 0 var(--space-3)',
                }}
              >
                <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', whiteSpace: 'nowrap' }}>
                  then, in this order
                </span>
                <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {sequential.length === 0 && (
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', margin: 0 }}>
                    Nobody signs in turn. Anyone added here waits for the person above them.
                  </p>
                )}
                {sequential.map((r, idx) => (
                  <SignerRow
                    key={r.key}
                    row={r}
                    idx={idx}
                    ordinal={idx + 1}
                    onChange={(patch) =>
                      setSequential((prev) =>
                        prev.map((x) => (x.key === r.key ? { ...x, ...patch } : x)),
                      )
                    }
                    onRemove={() => setSequential((prev) => prev.filter((x) => x.key !== r.key))}
                    onMove={() => {
                      setSequential((prev) => prev.filter((x) => x.key !== r.key));
                      setResidents((prev) => [...prev, r]);
                    }}
                    moveLabel="Move above the line"
                    onUp={
                      idx === 0
                        ? undefined
                        : () =>
                            setSequential((prev) => {
                              const next = [...prev];
                              [next[idx - 1], next[idx]] = [next[idx]!, next[idx - 1]!];
                              return next;
                            })
                    }
                    onDown={
                      idx === sequential.length - 1
                        ? undefined
                        : () =>
                            setSequential((prev) => {
                              const next = [...prev];
                              [next[idx], next[idx + 1]] = [next[idx + 1]!, next[idx]!];
                              return next;
                            })
                    }
                  />
                ))}
                <button
                  type="button"
                  className="btn"
                  data-variant="secondary"
                  data-size="sm"
                  onClick={() => setSequential((prev) => [...prev, blankRow()])}
                  style={{ alignSelf: 'flex-start' }}
                >
                  + Add signer in turn
                </button>
              </div>
            </>
          )}
          <label className="cluster" style={{ gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
            <input
              type="checkbox"
              checked={includeWhp}
              onChange={(e) => setIncludeWhp(e.target.checked)}
            />
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--fg)' }}>
              Include WHP as a signer (countersigns after the residents)
            </span>
          </label>
        </section>

        {/* 3. Document details + upload */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Document</h3>
          </div>
          <div className="stack-sm">
            <div className="field">
              <label htmlFor="manual-name">Document name <span aria-hidden>*</span></label>
              <input
                id="manual-name"
                type="text"
                className="input"
                placeholder="e.g. Parking Addendum"
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                maxLength={120}
              />
              <span className="helper">Shown to signers (the email + signing page name this document).</span>
            </div>
            <div className="field">
              <label htmlFor="manual-desc">Description (optional)</label>
              <input
                id="manual-desc"
                type="text"
                className="input"
                placeholder="One line shown in the request email."
                value={documentDescription}
                onChange={(e) => setDocumentDescription(e.target.value)}
                maxLength={240}
              />
            </div>
            <div className="field">
              <label htmlFor="manual-file">PDF file <span aria-hidden>*</span></label>
              <input
                id="manual-file"
                type="file"
                className="input"
                accept="application/pdf,.pdf"
                onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
              />
              <span className="helper">PDF only, up to 50 MB.</span>
              {pdfError && <span className="helper" data-state="error">{pdfError}</span>}
            </div>
          </div>
        </section>

        {/* 4. Place fields */}
        {pdfDoc && (
          <section className="card">
            <div className="card-header">
              <h3 className="card-title">Place signature fields</h3>
            </div>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', margin: '0 0 var(--space-3)' }}>
              Choose a field type and a signer, then DRAG a box onto the page (or click to drop a
              default-sized box). Each box is assigned to the signer selected when you place it.
            </p>

            {/* Field-type + signer toolbar */}
            <div
              className="cluster"
              style={{ gap: 'var(--space-4)', marginBottom: 'var(--space-4)', flexWrap: 'wrap', alignItems: 'flex-end' }}
            >
              <div className="field" style={{ minWidth: 160 }}>
                <label htmlFor="field-type">Field type</label>
                <select
                  id="field-type"
                  className="input"
                  value={activeType}
                  onChange={(e) => setActiveType(e.target.value as ManualDocFieldType)}
                >
                  {MANUAL_DOC_FIELD_TYPES.map((t) => (
                    <option key={t} value={t}>{FIELD_TYPE_LABEL[t]}</option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ minWidth: 200 }}>
                <label htmlFor="field-signer">Assign to</label>
                <select
                  id="field-signer"
                  className="input"
                  value={String(activeSigner)}
                  onChange={(e) =>
                    setActiveSigner(e.target.value === 'whp' ? 'whp' : Number(e.target.value))
                  }
                  disabled={signerOptions.length === 0}
                >
                  {signerOptions.length === 0 && <option value="">Add a signer first…</option>}
                  {signerOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }}>
                {fields.length} field{fields.length === 1 ? '' : 's'} placed
              </div>
            </div>

            {fields.length === 0 && (
              <div className="alert" data-tone="info" role="note" style={{ marginBottom: 'var(--space-3)' }}>
                <div className="alert-body">
                  Place at least one field — drag a box onto a page below.
                </div>
              </div>
            )}

            <div style={{ overflowX: 'auto' }}>
              {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNumber) => (
                <PdfPage
                  key={pageNumber}
                  doc={pdfDoc}
                  pageNumber={pageNumber}
                  fields={fields}
                  residentKeys={residentKeys}
                  signerLabelFor={signerLabelFor}
                  activeType={activeType}
                  activeSigner={activeSigner}
                  onPlace={addField}
                  onRemove={removeField}
                />
              ))}
            </div>
          </section>
        )}

        {/* 5. Send */}
        <section className="card">
          <div className="stack-sm">
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              The residents are emailed their branded signing links now.
              {includeWhp ? ' WHP is invited to countersign once every resident has signed.' : ''}
            </p>
            {residents.length > 0 && !everySignerValid && (
              <div className="alert" data-tone="warning" role="note">
                <div className="alert-body">
                  Every signer needs a first name and a valid email before you can send. Fix or
                  remove (×) any signer above — none are dropped silently.
                </div>
              </div>
            )}
            {file && advisory.status === 'checking' && (
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', margin: 0 }}>
                Checking the PDF against the selected property and for prior signatures…
              </p>
            )}
            {advisory.status === 'done' && advisory.warnings.length > 0 && (
              <div className="alert" data-tone="warning" role="note">
                <div className="alert-body">
                  <strong>Check this document before sending:</strong>
                  <ul style={{ margin: 'var(--space-2) 0 0', paddingLeft: '1.2em' }}>
                    {advisory.warnings.map((w) => (
                      <li key={w.code}>{w.message}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            <button
              type="button"
              className="btn"
              onClick={() => void handleSend()}
              disabled={!canSend || advisoryWait}
              style={{ alignSelf: 'flex-start', marginTop: 'var(--space-2)' }}
            >
              {busy ? 'Sending…' : advisoryWait ? 'Checking document…' : 'Send for signing'}
            </button>
          </div>
        </section>
      </div>

      {/* Pre-send advisory modal — warnings + "Send anyway" (override is logged). */}
      <ConfirmDialog
        open={advisoryModalOpen}
        onOpenChange={(open) => {
          if (!busy) setAdvisoryModalOpen(open);
        }}
        title="Double-check before sending"
        description="These checks don't block you — review them and decide."
        confirmLabel="Send anyway"
        busy={busy}
        busyLabel="Sending…"
        tone="danger"
        onConfirm={confirmAdvisoryOverride}
      >
        <ul style={{ margin: '0 0 var(--space-4)', paddingLeft: '1.2em' }}>
          {advisory.warnings.map((w) => (
            <li key={w.code} style={{ marginBottom: 'var(--space-3)' }}>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--fg)' }}>{w.message}</span>
              {w.detail && w.detail.length > 0 && (
                <ul style={{ margin: 'var(--space-1) 0 0', paddingLeft: '1.1em' }}>
                  {w.detail.map((d, i) => (
                    <li key={i} style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }}>
                      {d}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
        <div className="field">
          <label htmlFor="advisory-note">Why send anyway? (optional — kept in the audit log)</label>
          <textarea
            id="advisory-note"
            className="input"
            rows={2}
            maxLength={500}
            value={overrideNote}
            onChange={(e) => setOverrideNote(e.target.value)}
            placeholder="e.g. Confirmed with the tenant — this report is for their unit."
          />
        </div>
      </ConfirmDialog>
    </div>
  );
}
